import { useId } from 'react';
import type { ReactNode } from 'react';
import { Bouton } from './composants.tsx';

/** Les trois tailles de page proposées. La première sert aussi de seuil
 *  d'affichage : sous vingt-cinq éléments, aucun contrôle ne s'affiche. */
export const TAILLES = [25, 50, 100] as const;

/**
 * Éléments de mise en page.
 *
 * Ils existent pour une raison précise : chaque écran métier avait recomposé à
 * la main son en-tête, sa barre d'outils et ses filtres, avec à chaque fois des
 * espacements légèrement différents. L'œil le voit — c'est ce qui donne
 * l'impression d'une application « faite à la va-vite » même quand chaque écran
 * pris isolément semble correct.
 */

/**
 * En-tête d'écran : titre, contexte, actions.
 *
 * ── Le contexte est aussi important que le titre ────────────────────────────
 * « Élèves » ne dit rien ; « 2026-2027 · 24 élèves » dit sur quoi on travaille.
 * Dans une application multi-années, l'oubli de ce rappel fait saisir dans la
 * mauvaise année — erreur qu'on ne détecte que des semaines plus tard.
 */
export function EnTetePage({
  titre, contexte, actions, children,
}: {
  titre: string;
  contexte?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1>{titre}</h1>
          {contexte && (
            <p className="mt-1 text-[13px] leading-snug text-[var(--color-fg-muted)]">
              {contexte}
            </p>
          )}
        </div>
        {/* `print:hidden` : les boutons n'ont aucun sens sur une feuille. */}
        {actions && (
          <div className="flex flex-wrap items-center gap-2 print:hidden">{actions}</div>
        )}
      </div>
      {children}
    </header>
  );
}

/**
 * Barre d'outils : filtres, navigation temporelle, réglages d'affichage.
 *
 * Séparée de l'en-tête parce qu'elle ne s'imprime pas et qu'elle peut être
 * volumineuse — les mélanger produit un en-tête qui pousse le contenu hors de
 * l'écran sur un portable.
 */
/*
 * ── Pourquoi `items-end`, et ce qu'il ne faut pas y mettre ──────────────────
 * Les enfants n'ont pas tous la même structure : un `GroupeBoutons` est un
 * contrôle nu de 38 px, un `Champ` porte un libellé au-dessus. Aligner par le
 * BAS est le seul réglage qui fasse coïncider les contrôles eux-mêmes.
 *
 * D'où une règle : PAS de texte d'aide sous un champ de barre d'outils. Il
 * s'ajoute sous le contrôle, l'alignement se fait alors sur le bas de l'aide,
 * et le champ remonte de quinze pixels par rapport à ses voisins — c'est
 * exactement ce qui décalait les filtres des écrans Clients, Support et Audit.
 *
 * Le remplaçant est le `placeholder` : il tient DANS le contrôle, ne change
 * aucune hauteur, et disparaît dès qu'on tape — c'est-à-dire au moment précis
 * où l'indication cesse d'être utile.
 */
export function BarreOutils({ children }: { children: ReactNode }) {
  return (
    <div
      className={[
        'flex flex-wrap items-end gap-2 rounded-xl border p-2.5 print:hidden',
        'border-[var(--color-border)] bg-[var(--color-surface)]',
        'shadow-[var(--ombre-carte)]',
      ].join(' ')}
    >
      {children}
    </div>
  );
}

/** Pousse ce qui suit à droite dans une barre d'outils. */
export function Espaceur() {
  return <div className="flex-1" />;
}

/**
 * Groupe de boutons exclusifs — bascule entre deux ou trois vues.
 *
 * `role="group"` et non `radiogroup` : ce sont des boutons qui agissent, pas
 * des champs de formulaire. `aria-pressed` porte l'état, ce qui permet à un
 * lecteur d'écran d'annoncer « Semaine, activé ».
 */
export function GroupeBoutons<T extends string>({
  legende, legendeVisible = false, valeur, options, surChangement,
}: {
  legende: string;
  /**
   * Affiche la légende AU-DESSUS du groupe, comme le libellé d'un champ.
   *
   * Sans elle, un groupe posé à côté d'un `<select>` libellé paraît orphelin :
   * la barre montre « Catégorie » d'un côté et deux boutons nus de l'autre, et
   * rien ne dit ce que ces boutons choisissent.
   */
  legendeVisible?: boolean;
  valeur: T;
  options: ReadonlyArray<{ valeur: T; texte: string; titre?: string; icone?: ReactNode }>;
  surChangement: (v: T) => void;
}) {
  const idLegende = useId();

  const groupe = (
    <div
      role="group"
      /*
       * Légende visible : `aria-labelledby` la désigne, plutôt que de répéter
       * le texte dans `aria-label`. Deux libellés pour un même groupe, c'est
       * un risque de les voir diverger — et le lecteur d'écran n'en annonce
       * qu'un.
       */
      {...(legendeVisible
        ? { 'aria-labelledby': idLegende }
        : { 'aria-label': legende })}
      // Fond creusé, pastille en relief sur l'option active : c'est le motif
      // que tout le monde reconnaît comme « bascule », sans légende.
      className="inline-flex h-[38px] items-center gap-0.5 rounded-lg bg-[var(--color-surface-raised)] p-1 ring-1 ring-inset ring-[var(--color-border)]"
    >
      {options.map((o) => (
        <button
          key={o.valeur}
          type="button"
          onClick={() => surChangement(o.valeur)}
          aria-pressed={valeur === o.valeur}
          {...(o.titre ? { title: o.titre } : {})}
          className={[
            'inline-flex h-full items-center gap-1.5 rounded-md px-2.5',
            'text-[13px] font-medium leading-none transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-[var(--color-focus)]',
            valeur === o.valeur
              ? 'bg-[var(--color-surface)] text-[var(--color-fg)] shadow-[0_1px_2px_rgba(20,24,31,.08)]'
              : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]',
          ].join(' ')}
        >
          {o.icone}
          {o.texte}
        </button>
      ))}
    </div>
  );

  if (!legendeVisible) return groupe;

  // Même gabarit que le libellé d'un champ (`Habillage`) : posés côte à côte
  // dans une barre d'outils, les deux doivent s'aligner au pixel.
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span
        id={idLegende}
        className="text-[13px] font-medium leading-none text-[var(--color-fg)]"
      >
        {legende}
      </span>
      {groupe}
    </div>
  );
}

/**
 * Pastille de couleur d'une matière, avec son nom.
 *
 * La couleur ne porte JAMAIS l'information seule : le nom l'accompagne
 * toujours. Une grille d'emploi du temps où les matières ne se distinguent que
 * par la teinte est illisible pour 8 % des hommes.
 */
export function BadgeMatiere({
  nom, couleur, abrege, taille = 'normale',
}: {
  nom: string;
  couleur: string;
  abrege?: string;
  taille?: 'compacte' | 'normale';
}) {
  const texte = taille === 'compacte' && abrege ? abrege : nom;
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-md font-medium',
        'ring-1 ring-inset ring-[var(--color-border)]',
        'bg-[var(--color-surface-raised)] text-[var(--color-fg)]',
        taille === 'compacte' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-[13px]',
      ].join(' ')}
      title={taille === 'compacte' && abrege ? nom : undefined}
    >
      <span
        aria-hidden="true"
        className="inline-block size-2 shrink-0 rounded-full ring-1 ring-inset ring-black/15"
        style={{ backgroundColor: couleur }}
      />
      {texte}
    </span>
  );
}

/**
 * Grille de statistiques.
 *
 * Un chiffre seul ne dit rien : il lui faut son libellé, et parfois sa borne
 * (« 118 / 120 »). Les aligner sur une grille évite qu'ils ne paraissent
 * flotter.
 */
export function Chiffres({ children }: { children: ReactNode }) {
  return <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</dl>;
}

export function Chiffre({
  libelle, valeur, detail, ton, icone,
}: {
  libelle: string;
  valeur: ReactNode;
  detail?: ReactNode;
  ton?: 'attention' | 'erreur' | 'succes';
  icone?: ReactNode;
}) {
  return (
    <div
      className={[
        'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]',
        'p-4 shadow-[var(--ombre-carte)]',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <dt className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
          {libelle}
        </dt>
        {icone && (
          <span aria-hidden="true" className="text-[var(--color-fg-muted)]">{icone}</span>
        )}
      </div>
      <dd
        className={[
          'mt-1.5 text-[26px] font-semibold tabular-nums leading-none tracking-tight',
          ton === 'erreur' ? 'text-[var(--color-danger)]'
            : ton === 'attention' ? 'text-[var(--color-warning)]'
              : ton === 'succes' ? 'text-[var(--color-success)]'
                : 'text-[var(--color-fg)]',
        ].join(' ')}
      >
        {valeur}
      </dd>
      {detail && (
        <p className="mt-1.5 text-xs leading-snug text-[var(--color-fg-muted)]">{detail}</p>
      )}
    </div>
  );
}

/**
 * Grille de formulaire.
 *
 * Deux colonnes au-delà de 640 px, une en dessous. `sur2` fait occuper toute
 * la largeur à un champ — pour une zone de texte ou un champ long, qu'une
 * demi-largeur rendrait inconfortable.
 */
/**
 * Grille de champs de formulaire.
 *
 * ── Pourquoi le nombre de colonnes est un paramètre ─────────────────────────
 * Deux colonnes couvrent l'essentiel : un libellé et une valeur, deux dates.
 * Une saisie qui va par TROIS — un intitulé de période et ses deux bornes —
 * s'y repliait sur deux lignes, et la troisième arrivait sans son intitulé.
 * Sur cinq périodes à la suite, on ne savait plus quelle date allait avec
 * quoi.
 *
 * Trois colonnes plutôt qu'un `div` habillé sur place : c'est ainsi que
 * l'espacement d'un écran cesse de ressembler à celui des autres.
 */
export function GrilleFormulaire(
  { children, colonnes = 2 }: { children: ReactNode; colonnes?: 2 | 3 },
) {
  // Classes ÉCRITES EN ENTIER : Tailwind analyse le source, et une classe
  // composée à l'exécution (`sm:grid-cols-${n}`) ne serait jamais générée.
  const grille = colonnes === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
  return <div className={`grid gap-4 ${grille}`}>{children}</div>;
}

export function Sur2({ children }: { children: ReactNode }) {
  return <div className="sm:col-span-2">{children}</div>;
}

// ── Jauge ────────────────────────────────────────────────────────────────────

/**
 * Avancement vers un objectif chiffré.
 *
 * ── Pourquoi c'est une primitive et non un dessin d'écran ───────────────────
 * Une barre d'avancement se redessine à la main en trois lignes de CSS, et
 * c'est bien le problème : la deuxième ne ressemble jamais à la première —
 * hauteur, rayon, couleur du fond, comportement au dépassement. Le module des
 * 108 heures en affiche cinq côte à côte ; la facturation en voudra une pour
 * les sièges consommés.
 *
 * ── Le DÉPASSEMENT est un état à part, pas une barre pleine ─────────────────
 * Une jauge qui plafonne à 100 % dit la même chose à qui a fait 108 heures et
 * à qui en a fait 140. Or ce n'est pas la même situation : la seconde mérite
 * d'être vue. La barre reste bornée — au-delà, elle déborderait de son cadre —
 * mais l'état « atteint » change sa couleur, et le libellé dit l'excédent.
 *
 * ── Ce qu'elle ne fait pas ──────────────────────────────────────────────────
 * Elle ne met rien en forme : `valeur`, `cible` et `libelle` arrivent déjà
 * rendus. Une jauge qui saurait formater des minutes en heures ne servirait
 * qu'aux 108 heures, et il faudrait la contourner partout ailleurs.
 */
export function Jauge({
  titre, valeur, cible, libelle, pourcentage, atteint, couleur,
}: {
  titre: ReactNode;
  /** Ce qui est fait, déjà mis en forme. */
  valeur: ReactNode;
  /** L'objectif, déjà mis en forme. */
  cible: ReactNode;
  /** Une ligne sous la barre : ce qui reste, ou l'excédent. */
  libelle?: ReactNode;
  /** 0 à 100. Borné par l'appelant, qui seul sait ce que « plein » veut dire. */
  pourcentage: number;
  /** Objectif atteint : la barre passe au vert, quel que soit `couleur`. */
  atteint?: boolean;
  /** Teinte de la barre. Absente, la couleur d'accent de l'application. */
  couleur?: string;
}) {
  const largeur = Math.max(0, Math.min(100, pourcentage));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className="flex min-w-0 items-center gap-1.5 font-medium">
          {couleur && (
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ background: couleur }}
            />
          )}
          <span className="truncate">{titre}</span>
        </span>
        <span className="shrink-0 tabular-nums font-semibold">
          {valeur}
          <span className="font-normal text-[var(--color-fg-muted)]"> / {cible}</span>
        </span>
      </div>

      <div
        // `role="img"` plutôt qu'une barre ARIA : le texte au-dessus porte déjà
        // les valeurs, et un lecteur d'écran les entendrait deux fois.
        role="img"
        aria-label={`${largeur} %`}
        className="h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-hover)]"
      >
        <span
          className="block h-full rounded-full transition-[width] duration-300"
          style={{
            width: `${largeur}%`,
            background: atteint ? 'var(--color-success)' : (couleur ?? 'var(--color-primary)'),
          }}
        />
      </div>

      {libelle && (
        <div
          className={[
            'text-[12px]',
            atteint ? 'text-[var(--color-success)]' : 'text-[var(--color-fg-muted)]',
          ].join(' ')}
        >
          {libelle}
        </div>
      )}
    </div>
  );
}

/**
 * Pagination d'une liste : combien par page, et où l'on en est.
 *
 * ── Pourquoi une primitive, et non un bloc par écran ────────────────────────
 * Six listes du back-office rendaient tout d'un bloc. Écrire la pagination six
 * fois, c'est six libellés qui divergent, six seuils différents, et six endroits
 * où corriger le jour où l'un d'eux se trompe d'une ligne. Elle vit donc ici,
 * comme le reste du vocabulaire visuel.
 *
 * ── Vingt-cinq par défaut ───────────────────────────────────────────────────
 * Vingt-cinq lignes tiennent dans un écran sans défilement : on voit ce qu'on
 * traite, et l'on ne perd pas le haut de la liste en agissant sur le bas.
 * Cinquante et cent restent à portée pour les jours où l'on balaie.
 *
 * ── Le choix de taille reste TOUJOURS visible ───────────────────────────────
 * Il était masqué sous vingt-cinq éléments, au motif qu'un sélecteur au-dessus
 * de trois lignes est du bruit. C'était se tromper de lecteur : celui qui règle
 * « cent par page » le fait une fois, sur une liste courte, pour que la suivante
 * s'affiche comme il veut. Le masquer rendait le réglage introuvable — il ne
 * serait apparu qu'une fois le problème déjà là.
 *
 * Seuls « Précédente » et « Suivante » disparaissent quand il n'y a qu'une page :
 * ceux-là ne servent réellement à rien.
 */
export function Pagination({
  page, pages, parPage, total, surPage, surTaille,
}: {
  /** Page courante, à partir de zéro. */
  page: number;
  pages: number;
  parPage: number;
  /** Nombre total d'éléments, après filtrage. */
  total: number;
  surPage: (page: number) => void;
  surTaille: (parPage: number) => void;
}) {
  // Rien à paginer du tout : ni décompte, ni réglage.
  if (total === 0) return null;

  const premier = page * parPage + 1;
  const dernier = Math.min((page + 1) * parPage, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
      <span className="text-[13px] tabular-nums text-[var(--color-fg-muted)]">
        {`${premier}–${dernier} sur ${total}`}
      </span>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-[13px] text-[var(--color-fg-muted)]">
          Par page
          <select
            value={String(parPage)}
            onChange={(e) => surTaille(Number(e.target.value))}
            className={[
              'h-[30px] rounded-md border border-[var(--color-border)]',
              'bg-[var(--color-surface)] px-1.5 text-[13px] text-[var(--color-fg)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-[var(--color-focus)]',
            ].join(' ')}
          >
            {TAILLES.map((t) => (
              <option key={t} value={String(t)}>{t}</option>
            ))}
          </select>
        </label>

        {pages > 1 && (
          <>
            <Bouton
              variante="secondary"
              taille="sm"
              disabled={page === 0}
              onClick={() => surPage(page - 1)}
            >
              Précédente
            </Bouton>
            <span className="text-[13px] tabular-nums text-[var(--color-fg-muted)]">
              {`Page ${page + 1} sur ${pages}`}
            </span>
            <Bouton
              variante="secondary"
              taille="sm"
              disabled={page >= pages - 1}
              onClick={() => surPage(page + 1)}
            >
              Suivante
            </Bouton>
          </>
        )}
      </div>
    </div>
  );
}
